import { jsPDF } from 'jspdf';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })?.format(amount);
};

const getVerdictLabel = (type) => {
  const labels = {
    fair: 'Fair Pricing',
    aggressive: 'Aggressive Pricing',
    risky: 'High Risk',
    overpriced: 'Overpriced',
    underpriced: 'Underpriced',
  };
  return labels?.[type] || 'Analysis Complete';
};

const getVerdictColor = (type) => {
  const colors = {
    fair: [16, 185, 129],       // emerald/success
    underpriced: [16, 185, 129],
    aggressive: [217, 119, 6],  // amber/warning
    overpriced: [217, 119, 6],
    risky: [220, 38, 38],       // red/error
  };
  return colors?.[type] || [30, 64, 175]; // default primary blue
};

export const generateAnalysisPDF = (analysis) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const addPage = () => {
    doc?.addPage();
    y = margin;
    addPageFooter();
  };

  const checkPageBreak = (neededHeight) => {
    if (y + neededHeight > pageHeight - 25) {
      addPage();
    }
  };

  const addPageFooter = () => {
    const pageCount = doc?.internal?.getNumberOfPages();
    const currentPage = doc?.internal?.getCurrentPageInfo()?.pageNumber;
    doc?.setFontSize(8);
    doc?.setTextColor(150, 150, 150);
    doc?.setFont('helvetica', 'normal');
    doc?.text(
      `ListingLens Property Analysis  |  Page ${currentPage}`,
      margin,
      pageHeight - 10
    );
    doc?.text(
      `Confidential — For personal use only`,
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  };

  const drawSectionHeader = (title, iconChar, bgColor) => {
    checkPageBreak(18);
    doc?.setFillColor(...bgColor);
    doc?.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text(`${iconChar}  ${title}`, margin + 4, y + 8);
    y += 16;
  };

  const drawBulletItem = (text, bulletColor, bgColor, borderColor) => {
    const lines = doc?.splitTextToSize(text, contentWidth - 14);
    const itemHeight = lines?.length * 5 + 8;
    checkPageBreak(itemHeight);

    if (bgColor) {
      doc?.setFillColor(...bgColor);
      doc?.roundedRect(margin, y, contentWidth, itemHeight, 1.5, 1.5, 'F');
    }
    if (borderColor) {
      doc?.setDrawColor(...borderColor);
      doc?.setLineWidth(0.3);
      doc?.roundedRect(margin, y, contentWidth, itemHeight, 1.5, 1.5, 'S');
    }

    doc?.setFillColor(...bulletColor);
    doc?.circle(margin + 5, y + itemHeight / 2, 1.5, 'F');

    doc?.setFontSize(9.5);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(30, 30, 30);
    doc?.text(lines, margin + 10, y + 5.5);
    y += itemHeight + 3;
  };

  const drawNumberedItem = (index, text, bulletColor, bgColor) => {
    const lines = doc?.splitTextToSize(text, contentWidth - 14);
    const itemHeight = lines?.length * 5 + 8;
    checkPageBreak(itemHeight);

    if (bgColor) {
      doc?.setFillColor(...bgColor);
      doc?.roundedRect(margin, y, contentWidth, itemHeight, 1.5, 1.5, 'F');
    }

    doc?.setFillColor(...bulletColor);
    doc?.circle(margin + 5, y + itemHeight / 2, 3.5, 'F');
    doc?.setFontSize(7);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text(String(index + 1), margin + 5, y + itemHeight / 2 + 2.5, { align: 'center' });

    doc?.setFontSize(9.5);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(30, 30, 30);
    doc?.text(lines, margin + 12, y + 5.5);
    y += itemHeight + 3;
  };

  // ─── HEADER BANNER ──────────────────────────────────────────────────────────
  doc?.setFillColor(30, 64, 175); // primary blue
  doc?.rect(0, 0, pageWidth, 38, 'F');

  // Brand name
  doc?.setFontSize(22);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(255, 255, 255);
  doc?.text('ListingLens', margin, 18);

  // Tagline
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(147, 197, 253); // light blue
  doc?.text('AI-Powered Property Analysis', margin, 26);

  // Report label on right
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(255, 255, 255);
  doc?.text('PROPERTY ANALYSIS REPORT', pageWidth - margin, 18, { align: 'right' });
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(147, 197, 253);
  doc?.text(
    `Generated: ${new Date(analysis?.analysisDate)?.toLocaleDateString('en-AU', {
      day: '2-digit', month: 'long', year: 'numeric'
    })}`,
    pageWidth - margin,
    26,
    { align: 'right' }
  );

  y = 46;

  // ─── PROPERTY DETAILS CARD ──────────────────────────────────────────────────
  doc?.setFillColor(248, 250, 252);
  doc?.roundedRect(margin, y, contentWidth, 42, 3, 3, 'F');
  doc?.setDrawColor(226, 232, 240);
  doc?.setLineWidth(0.4);
  doc?.roundedRect(margin, y, contentWidth, 42, 3, 3, 'S');

  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(15, 23, 42);
  doc?.text(analysis?.property?.address || 'Property Address', margin + 6, y + 12);

  const suburbLine = [
    analysis?.property?.suburb,
    analysis?.property?.state,
    analysis?.property?.postcode,
  ]?.filter(Boolean)?.join(', ');

  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(100, 116, 139);
  doc?.text(suburbLine, margin + 6, y + 20);

  // Property specs row
  const specs = [];
  if (analysis?.property?.bedrooms) specs?.push(`Bed: ${analysis?.property?.bedrooms}`);
  if (analysis?.property?.bathrooms) specs?.push(`Bath: ${analysis?.property?.bathrooms}`);
  if (analysis?.property?.parking) specs?.push(`Park: ${analysis?.property?.parking}`);
  if (analysis?.property?.pricingGuide) specs?.push(`Guide: ${analysis?.property?.pricingGuide}`);

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(30, 64, 175);
  doc?.text(specs?.join('   |   '), margin + 6, y + 30);

  // Analysis ID
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(150, 150, 150);
  doc?.text(`Analysis ID: ${analysis?.id || 'N/A'}`, margin + 6, y + 38);

  y += 50;

  // ─── VERDICT SECTION ────────────────────────────────────────────────────────
  const verdictType = analysis?.verdict?.type || 'fair';
  const verdictColor = getVerdictColor(verdictType);
  const verdictLabel = getVerdictLabel(verdictType);
  const confidence = analysis?.confidence?.verdict || analysis?.confidence?.overall || 0;

  doc?.setFillColor(...verdictColor);
  doc?.roundedRect(margin, y, contentWidth, 14, 3, 3, 'F');

  doc?.setFontSize(13);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(255, 255, 255);
  doc?.text(`VERDICT: ${verdictLabel?.toUpperCase()}`, margin + 6, y + 9.5);

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text(`Confidence: ${confidence}%`, pageWidth - margin - 4, y + 9.5, { align: 'right' });

  y += 18;

  // Verdict summary text
  if (analysis?.verdict?.summary) {
    const summaryLines = doc?.splitTextToSize(analysis?.verdict?.summary, contentWidth);
    const summaryHeight = summaryLines?.length * 5 + 10;
    checkPageBreak(summaryHeight);

    doc?.setFillColor(248, 250, 252);
    doc?.roundedRect(margin, y, contentWidth, summaryHeight, 2, 2, 'F');
    doc?.setDrawColor(...verdictColor, 80);
    doc?.setLineWidth(0.3);
    doc?.roundedRect(margin, y, contentWidth, summaryHeight, 2, 2, 'S');

    doc?.setFontSize(9.5);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(30, 30, 30);
    doc?.text(summaryLines, margin + 5, y + 7);
    y += summaryHeight + 8;
  }

  // ─── OFFER BAND ─────────────────────────────────────────────────────────────
  checkPageBreak(50);
  drawSectionHeader('RECOMMENDED OFFER BAND', '$', [245, 158, 11]);

  const offerMin = formatCurrency(analysis?.offerBand?.min);
  const offerMax = formatCurrency(analysis?.offerBand?.max);
  const spread = analysis?.offerBand?.min && analysis?.offerBand?.max
    ? formatCurrency(analysis?.offerBand?.max - analysis?.offerBand?.min)
    : 'N/A';

  const offerCardWidth = (contentWidth - 6) / 2;

  // Min offer card
  doc?.setFillColor(236, 253, 245);
  doc?.roundedRect(margin, y, offerCardWidth, 22, 2, 2, 'F');
  doc?.setDrawColor(16, 185, 129);
  doc?.setLineWidth(0.4);
  doc?.roundedRect(margin, y, offerCardWidth, 22, 2, 2, 'S');
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(100, 116, 139);
  doc?.text('Minimum Offer', margin + 4, y + 7);
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(16, 185, 129);
  doc?.text(offerMin, margin + 4, y + 17);

  // Max offer card
  const maxX = margin + offerCardWidth + 6;
  doc?.setFillColor(255, 251, 235);
  doc?.roundedRect(maxX, y, offerCardWidth, 22, 2, 2, 'F');
  doc?.setDrawColor(217, 119, 6);
  doc?.setLineWidth(0.4);
  doc?.roundedRect(maxX, y, offerCardWidth, 22, 2, 2, 'S');
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(100, 116, 139);
  doc?.text('Maximum Offer', maxX + 4, y + 7);
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(217, 119, 6);
  doc?.text(offerMax, maxX + 4, y + 17);

  y += 26;

  // Spread info
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(100, 116, 139);
  doc?.text(`Negotiation spread: ${spread}  |  Confidence: ${analysis?.confidence?.offerBand || 0}%`, margin, y);
  y += 8;

  // Negotiation tip
  checkPageBreak(18);
  doc?.setFillColor(239, 246, 255);
  doc?.roundedRect(margin, y, contentWidth, 16, 2, 2, 'F');
  doc?.setFontSize(8.5);
  doc?.setFont('helvetica', 'italic');
  doc?.setTextColor(30, 64, 175);
  const tipText = 'Tip: Start at the lower end of this band and negotiate up. Consider days on market and vendor motivation.';
  const tipLines = doc?.splitTextToSize(tipText, contentWidth - 8);
  doc?.text(tipLines, margin + 4, y + 6);
  y += 20;

  // ─── STRENGTHS ──────────────────────────────────────────────────────────────
  checkPageBreak(20);
  drawSectionHeader('PROPERTY STRENGTHS', '+', [16, 185, 129]);

  (analysis?.strengths || [])?.forEach((s) => {
    drawBulletItem(s, [16, 185, 129], [236, 253, 245], [167, 243, 208]);
  });

  y += 4;

  // ─── RISKS ──────────────────────────────────────────────────────────────────
  checkPageBreak(20);
  drawSectionHeader('RISKS & UNKNOWNS', '!', [217, 119, 6]);

  (analysis?.risks || [])?.forEach((r) => {
    drawBulletItem(r, [217, 119, 6], [255, 251, 235], [253, 230, 138]);
  });

  y += 4;

  // ─── NEXT STEPS ─────────────────────────────────────────────────────────────
  checkPageBreak(20);
  drawSectionHeader('RECOMMENDED NEXT STEPS', '>', [30, 64, 175]);

  (analysis?.nextSteps || [])?.forEach((step, i) => {
    drawNumberedItem(i, step, [30, 64, 175], [239, 246, 255]);
  });

  y += 4;

  // ─── QUESTIONS FOR AGENT ────────────────────────────────────────────────────
  if (analysis?.questionsForAgent?.length) {
    checkPageBreak(20);
    drawSectionHeader('QUESTIONS TO ASK THE AGENT', '?', [99, 102, 241]);

    (analysis?.questionsForAgent)?.forEach((q, i) => {
      drawNumberedItem(i, q, [99, 102, 241], [238, 242, 255]);
    });

    y += 4;
  }

  // ─── DISCLAIMER ─────────────────────────────────────────────────────────────
  checkPageBreak(24);
  doc?.setFillColor(248, 250, 252);
  doc?.roundedRect(margin, y, contentWidth, 22, 2, 2, 'F');
  doc?.setDrawColor(226, 232, 240);
  doc?.setLineWidth(0.3);
  doc?.roundedRect(margin, y, contentWidth, 22, 2, 2, 'S');
  doc?.setFontSize(7.5);
  doc?.setFont('helvetica', 'italic');
  doc?.setTextColor(150, 150, 150);
  const disclaimer = 'DISCLAIMER: This report is generated by ListingLens AI and is intended for informational purposes only. It does not constitute financial, legal, or investment advice. Always consult a licensed real estate professional, financial advisor, and/or solicitor before making any property purchase decision.';
  const disclaimerLines = doc?.splitTextToSize(disclaimer, contentWidth - 8);
  doc?.text(disclaimerLines, margin + 4, y + 6);
  y += 26;

  // Add footer to first page
  addPageFooter();

  // ─── SAVE ───────────────────────────────────────────────────────────────────
  const addressSlug = (analysis?.property?.address || 'property')?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/^-|-$/g, '');
  const dateSlug = new Date(analysis?.analysisDate)?.toISOString()?.slice(0, 10);
  const filename = `listinglens-analysis-${addressSlug}-${dateSlug}.pdf`;

  doc?.save(filename);
};

export default generateAnalysisPDF;
